import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { cn } from '@/lib/utils';

function Drawer({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Root>) {
  return <DrawerPrimitive.Root data-slot='drawer' {...props} />;
}

function DrawerTrigger({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot='drawer-trigger' {...props} />;
}

function DrawerPortal({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
  return <DrawerPrimitive.Portal data-slot='drawer-portal' {...props} />;
}

function DrawerClose({ ...props }: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot='drawer-close' {...props} />;
}

function DrawerOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Overlay>) {
  return (
    <DrawerPrimitive.Overlay
      data-slot='drawer-overlay'
      className={cn(
        'hc:data-[state=open]:animate-in hc:data-[state=closed]:animate-out hc:data-[state=closed]:fade-out-0 hc:data-[state=open]:fade-in-0 hc:fixed hc:inset-0 hc:z-50 hc:bg-black/50',
        className
      )}
      {...props}
    />
  );
}

function DrawerContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Content>) {
  return (
    <DrawerPortal data-slot='drawer-portal'>
      <DrawerOverlay />
      <DrawerPrimitive.Content
        data-slot='drawer-content'
        className={cn(
          'hc:group/drawer-content hc:bg-background hc:fixed hc:z-50 hc:flex hc:h-auto hc:flex-col',
          'hc:data-[vaul-drawer-direction=top]:inset-x-0 hc:data-[vaul-drawer-direction=top]:top-0 hc:data-[vaul-drawer-direction=top]:mb-24 hc:data-[vaul-drawer-direction=top]:max-h-[80vh] hc:data-[vaul-drawer-direction=top]:rounded-b-lg hc:data-[vaul-drawer-direction=top]:border-b',
          'hc:data-[vaul-drawer-direction=bottom]:inset-x-0 hc:data-[vaul-drawer-direction=bottom]:bottom-0 hc:data-[vaul-drawer-direction=bottom]:mt-24 hc:data-[vaul-drawer-direction=bottom]:max-h-[80vh] hc:data-[vaul-drawer-direction=bottom]:rounded-t-lg hc:data-[vaul-drawer-direction=bottom]:border-t',
          'hc:data-[vaul-drawer-direction=right]:inset-y-0 hc:data-[vaul-drawer-direction=right]:right-0 hc:data-[vaul-drawer-direction=right]:w-3/4 hc:data-[vaul-drawer-direction=right]:border-l hc:data-[vaul-drawer-direction=right]:sm:max-w-sm',
          'hc:data-[vaul-drawer-direction=left]:inset-y-0 hc:data-[vaul-drawer-direction=left]:left-0 hc:data-[vaul-drawer-direction=left]:w-3/4 hc:data-[vaul-drawer-direction=left]:border-r hc:data-[vaul-drawer-direction=left]:sm:max-w-sm',
          className
        )}
        {...props}>
        <div className='hc:bg-muted hc:mx-auto hc:mt-4 hc:hidden hc:h-2 hc:w-[100px] hc:shrink-0 hc:rounded-full hc:group-data-[vaul-drawer-direction=bottom]/drawer-content:block' />
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
}

function DrawerHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='drawer-header'
      className={cn(
        'hc:flex hc:flex-col hc:gap-0.5 hc:p-4 hc:group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center hc:group-data-[vaul-drawer-direction=top]/drawer-content:text-center hc:md:gap-1.5 hc:md:text-left',
        className
      )}
      {...props}
    />
  );
}

function DrawerFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='drawer-footer'
      className={cn('hc:mt-auto hc:flex hc:flex-col hc:gap-2 hc:p-4', className)}
      {...props}
    />
  );
}

function DrawerTitle({ className, ...props }: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      data-slot='drawer-title'
      className={cn('hc:text-foreground hc:font-semibold', className)}
      {...props}
    />
  );
}

function DrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot='drawer-description'
      className={cn('hc:text-muted-foreground hc:text-sm', className)}
      {...props}
    />
  );
}

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
